import React, { Fragment, useMemo } from 'react';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';

import calendar from 'src/assets/images/icons/calendar.svg';
import dollar from 'src/assets/images/icons/dollar.svg';
import instance from 'src/assets/images/icons/instance.svg';
import receipt from 'src/assets/images/icons/receipt.svg';
import calendarTick from 'src/assets/images/icons/calender-tick.svg';
import information from 'src/assets/images/icons/information.svg';
import weight from 'src/assets/images/icons/weight.svg';
import { M3, Kg, Ton } from 'src/presentations/components/atom/unit';
import shipments from 'src/assets/images/icons/multiple-shipments.svg';
import { beautifyEnum, displayValueOrFallback, formatNumber, objectCountToArray } from 'src/utils/utility';
import { formatDateString } from 'src/utils/date';
import { useInvoiceSummaryFromParams } from 'src/use-cases/invoice/useInvoices';
import { ISectionCountProps } from 'src/utils/types';

const cardContainerStyle = {
  bgcolor: '#fff',
  borderRadius: '10px',
  border: '1px solid #DFDFDF',
  padding: '16px',
  mb: '10px',
  '@media (max-width: 1520px)': {
    padding: '12px',
  },
};
const cardContentStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
};
const cardContentStyleDate = {
  fontWeight: 700,
  color: '#8A92A6',
  fontSize: '16px',
  lineHeight: '20px',
  '@media (max-width: 1520px)': {
    fontSize: '12px',
    lineHeight: '16px',
  },
};
const cardDateStyle = {
  fontWeight: 700,
  fontSize: '16px',
  mt: '16px',
  '@media (max-width: 1520px)': {
    fontSize: '12px',
    mt: '12px',
  },
};

// const smallFontStyle = {
//   fontWeight: 400,
//   fontSize: '10px',
//   lineHeight: '16px',
// };

const mediumFontStyle = {
  fontWeight: 400,
  fontSize: '12px',
  lineHeight: '16px',
  '@media (max-width: 1520px)': {
    fontSize: '10px',
  },
};

const InvoiceDetails = () => {
  const { data } = useInvoiceSummaryFromParams();

  const deviationItems: ISectionCountProps = useMemo(() => objectCountToArray(data?.deviations ?? {}), [data]);
  // const shipmentItems: ISectionCountProps = useMemo(() => objectCountToArray(data?.servicesAndShipments ?? {}), [data]);

  // New grouping logic for ServiceType
  const groupedServiceTypes = useMemo(() => {
    if (!data?.serviceTypes) return [];

    const groups = data.serviceTypes.reduce((acc, service) => {
      let groupKey;
      let display;
      let displayType;

      if (service.name && service.specialType) {
        // Both name and specialType
        display = `${service.name} . ${service.specialType}`;
        groupKey = `name_${service.name}_special_${service.specialType}`;
        displayType = 'nameAndSpecial';
      } else if (service.name) {
        // Only name
        display = service.name;
        groupKey = `name_${service.name}`;
        displayType = 'nameOnly';
      } else if (service.specialType) {
        // Only specialType
        display = service.specialType;
        groupKey = `specialType_${service.specialType}`;
        displayType = 'specialOnly';
      } else {
        // Fallback to modality
        display = service.modality;
        groupKey = `modality_${service.modality}`;
        displayType = 'modalityOnly';
      }

      if (!acc[groupKey]) {
        acc[groupKey] = { count: 0, display, displayType };
      }
      acc[groupKey].count += 1;
      return acc;
    }, {});

    // Convert the grouped object into an array and embed the unique key
    return Object.entries(groups).map(([key, group]) => ({
      key,
      ...group,
    }));
  }, [data]);

  if (!data) {
    return 'Loading Invoice Detail';
  }

  return (
    <>
      {/* start: Top Card Inovice Details */}
      <Box data-testid='invoice-container' className='flex'>
        <Box className='gap-10 grid-cols-3 grid w-full'>
          {/* start: card Invoice date */}
          <Box
            sx={{
              ...cardContainerStyle,
            }}
          >
            <Box
              sx={{
                ...cardContentStyle,
              }}
            >
              <img src={calendar} alt='calendar' />
              <Box
                data-testid='invoice-date'
                sx={{
                  ...cardContentStyleDate,
                }}
              >
                Invoice Date
              </Box>
            </Box>
            <Box
              sx={{
                ...cardDateStyle,
              }}
              component='p'
            >
              {formatDateString(data?.invoiceDate)}
            </Box>
          </Box>

          {/* start: card Payment date */}
          <Box
            sx={{
              ...cardContainerStyle,
            }}
          >
            <Box
              sx={{
                ...cardContentStyle,
              }}
            >
              <img src={calendarTick} alt='calendarTick' />
              <Box
                data-testid='payment-date'
                sx={{
                  ...cardContentStyleDate,
                }}
              >
                Invoice Due Date
              </Box>
            </Box>
            <Box
              sx={{
                ...cardDateStyle,
              }}
              component='p'
            >
              {formatDateString(data?.paymentDate)}
            </Box>
          </Box>

          {/* start: card incoterm */}
          <Box
            sx={{
              ...cardContainerStyle,
            }}
          >
            <Box
              sx={{
                ...cardContentStyle,
              }}
            >
              <img src={receipt} alt='receipt' />
              <Box
                data-testid='incoterm'
                sx={{
                  ...cardContentStyleDate,
                }}
              >
                Billed Party
              </Box>
            </Box>
            <Box
              sx={{
                ...cardDateStyle,
              }}
              component='p'
            >
              {data?.invoicedParty ? beautifyEnum(data.invoicedParty).toUpperCase() : displayValueOrFallback(data?.invoicedParty)}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* start: Midle Card Inovice Details */}
      <Box data-testid='middle-card-invoice-details' className='flex'>
        <Box className='gap-10 grid-cols-2 grid w-full'>
          {/* start: Invoice Total Amount  */}
          <Box
            sx={{
              ...cardContainerStyle,
            }}
          >
            <Box
              sx={{
                ...cardContentStyle,
              }}
            >
              <img src={dollar} alt='dollar' />
              <Box
                data-testid='Invoice-Total-Amount'
                sx={{
                  ...cardContentStyleDate,
                }}
              >
                Invoice Total Amount
                <span className='text-10 text-blue_grey-50 font-normal block'>Before Audit</span>
              </Box>
            </Box>
            <Box className='flex mt-10 mb-4 justify-between items-center'>
              <Box
                data-testid='Before-Tax-JPY'
                component='p'
                sx={{
                  ...mediumFontStyle,
                }}
              >
                Before Tax - {data?.invoiceAmount?.currency}
              </Box>
              <Box component='p' sx={{ ...cardContentStyleDate }}>
                {formatNumber(data?.invoiceAmount?.beforeTax)}
              </Box>
            </Box>
            <Divider />
            <Box className='flex mt-8 justify-between items-center'>
              <Box
                component='p'
                sx={{
                  ...mediumFontStyle,
                }}
              >
                After Tax - {data?.invoiceAmount?.currency}
              </Box>
              <Box component='p' sx={{ ...cardContentStyleDate, color: '#000' }}>
                {formatNumber(data?.invoiceAmount?.afterTax)}
              </Box>
            </Box>
          </Box>

          {/* start: Deviations Total Amount  */}
          <Box
            sx={{
              ...cardContainerStyle,
            }}
          >
            <Box
              sx={{
                ...cardContentStyle,
              }}
            >
              <img src={dollar} alt='dollar' />
              <Box
                data-testid='Deviations-Total-Amount'
                sx={{
                  ...cardContentStyleDate,
                }}
              >
                Deviations Total Amount
                <span className='text-10 text-blue_grey-50 font-normal block'>Before Audit</span>
              </Box>
            </Box>
            <Box className='flex mt-10 mb-4 justify-between items-center'>
              <Box
                component='p'
                sx={{
                  ...mediumFontStyle,
                }}
              >
                Before Tax - {data?.invoiceAmountDeviation?.currency}
              </Box>
              <Box component='p' sx={{ ...cardContentStyleDate, color: '#E72547', opacity: '0.5' }}>
                {formatNumber(data?.invoiceAmountDeviation?.beforeTax)}
              </Box>
            </Box>
            <Divider />
            <Box className='flex mt-8 justify-between items-center'>
              <Box
                data-testid='After-Tax-JPY'
                component='p'
                sx={{
                  ...mediumFontStyle,
                }}
              >
                After Tax - {data?.invoiceAmountDeviation?.currency}
              </Box>
              <Box component='p' sx={{ ...cardContentStyleDate, color: '#E72547' }}>
                {formatNumber(data?.invoiceAmountDeviation?.afterTax)}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* start: Bottom Card Inovice Details */}
      <Box className='flex'>
        <Box className='gap-10 grid-cols-2 grid w-full'>
          <Box
            sx={{
              ...cardContainerStyle,
            }}
          >
            <Box
              sx={{
                ...cardContentStyle,
              }}
            >
              <img src={information} alt='information' />
              <Box
                data-testid='Deviations'
                sx={{
                  ...cardContentStyleDate,
                }}
              >
                {' '}
                Deviations
              </Box>
            </Box>

            <Box className='flex justify-between mt-10 bg-transparent'>
              <Box
                className='bg-transparent items-center text-center'
                component='p'
                sx={{ ...cardContentStyleDate, width: '32px', height: '32px', color: '#000' }}
              >
                {deviationItems?.total || 0}
              </Box>
              <Box>
                {deviationItems?.items?.map((item, index) => (
                  <Fragment key={`deviation-${item?.name}`}>
                    <Box
                      // data-testid='Unit-Price'
                      className='pb-4 pt-4'
                      component='p'
                      sx={{
                        ...mediumFontStyle,
                      }}
                    >
                      {item?.value} {item?.label === 'New Charge' ? 'Unknow Charge' : item?.label}
                    </Box>
                    {index < deviationItems?.items?.length - 1 && <Divider />}
                  </Fragment>
                ))}
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              ...cardContainerStyle,
            }}
          >
            <Box
              sx={{
                ...cardContentStyle,
              }}
            >
              <img src={shipments} alt='shipments' />
              <Box
                data-testid='Accounting-Details'
                sx={{
                  ...cardContentStyleDate,
                }}
              >
                Accounting Details
              </Box>
            </Box>
            <Box className='flex mt-10 mb-4 justify-between items-center'>
              <Box
                component='p'
                sx={{
                  ...mediumFontStyle,
                }}
              >
                Account Number
              </Box>
              <Box component='p' sx={{ ...cardContentStyleDate, color: '#000' }}>
                {data?.accountNumber || '-'}
              </Box>
            </Box>
            <Divider />
            <Box className='flex mt-8 justify-between items-center'>
              <Box
                data-testid='After-Tax-JPY'
                component='p'
                sx={{
                  ...mediumFontStyle,
                }}
              >
                Transportation PO#
              </Box>
              <Box component='p' sx={{ ...cardContentStyleDate, color: '#000' }}>
                {data?.fapRef || '-'}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* start: Very Bottom Card Inovice Details */}
      <Box className='flex'>
        <Box className='gap-10 grid-cols-2 grid w-full'>
          <Box
            sx={{
              ...cardContainerStyle,
            }}
          >
            <Box
              sx={{
                ...cardContentStyle,
              }}
            >
              <img src={instance} alt='information' />
              <Box
                data-testid='Services-Shipments'
                sx={{
                  ...cardContentStyleDate,
                }}
              >
                {' '}
                Services & Shipments
              </Box>
            </Box>

            <Box className='flex justify-between mt-10 bg-transparent'>
              <Box
                className='bg-transparent items-center text-center'
                component='p'
                sx={{ ...cardContentStyleDate, width: '32px', height: '32px', color: '#000' }}
              >
                {/* Sum up the counts of each group to show the total */}
                {groupedServiceTypes.reduce((sum, group) => sum + group.count, 0)}
              </Box>
              <Box className='text-right'>
                {groupedServiceTypes.map((group) => (
                  <React.Fragment key={group.key}>
                    <Box component='p' className='pb-4 pt-4 text-12'>
                      {group.displayType === 'modalityOnly' ? group.display : `${group.count} ${group.display}`}
                    </Box>
                    <Divider />
                  </React.Fragment>
                ))}
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              ...cardContainerStyle,
            }}
          >
            <Box
              sx={{
                ...cardContentStyle,
              }}
            >
              <img src={weight} alt='weight' />
              <Box
                data-testid='Total-Shipments-Metrics'
                sx={{
                  ...cardContentStyleDate,
                }}
              >
                Total Shipments Metrics
              </Box>
            </Box>
            <Box className='flex mt-10 mb-4 justify-between items-center'>
              <Box
                component='p'
                sx={{
                  ...mediumFontStyle,
                }}
              >
                Total Chargeable Weight
              </Box>
              <Box component='p' sx={{ ...cardContentStyleDate, color: '#000' }}>
                {formatNumber(data?.totalChargeableWeight)} <Kg />
              </Box>
            </Box>
            <Divider />
            <Box className='flex mt-8 justify-between items-center'>
              <Box
                data-testid='After-Tax-JPY'
                component='p'
                sx={{
                  ...mediumFontStyle,
                }}
              >
                Volume
              </Box>
              <Box component='p' sx={{ ...cardContentStyleDate, color: '#000' }}>
                {formatNumber(data?.volume)} <M3 />
              </Box>
            </Box>
            <Divider />
            <Box className='flex mt-8 justify-between items-center'>
              <Box
                data-testid='After-Tax-JPY'
                component='p'
                sx={{
                  ...mediumFontStyle,
                }}
              >
                Number of Packages
              </Box>
              <Box component='p' sx={{ ...cardContentStyleDate, color: '#000' }}>
                {data?.packageCount}
              </Box>
            </Box>
            <Divider />
            <Box className='flex mt-8 justify-between items-center'>
              <Box
                data-testid='After-Tax-JPY'
                component='p'
                sx={{
                  ...mediumFontStyle,
                }}
              >
                Total CO₂e
              </Box>
              <Box component='p' sx={{ ...cardContentStyleDate, color: '#000' }}>
                {data?.totalCo2Emission === 0 ? '0' : displayValueOrFallback(data?.totalCo2Emission)}
                {data?.totalCo2Emission ? <Ton /> : ''}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default InvoiceDetails;
